import { isDevice4GMeter } from './deviceMeter';

/** QK/NQK：表号参与指令拼接；4G/YJ 等优先用 MAC 查映射（与小程序 test1 一致） */
export function isQKOrNQKMeter(meterNo) {
  const m = String(meterNo || '').trim().toUpperCase();
  return m.startsWith('QK') || m.startsWith('NQK');
}

/** 下发 MQTT / publish-by-meter 时使用的 meter_no（与 device_topic_mapping 一致） */
export function getAdminMeterPublishKey(device) {
  if (!device) return '';
  const is4G = isDevice4GMeter(device);
  let mac = String(device.device_mac || '').trim();
  if (mac.includes(':')) mac = mac.replace(/:/g, '');
  const meterNo = String(device.current_meter_no || '').trim();
  const useMac = (is4G || !isQKOrNQKMeter(meterNo)) && mac;
  const key = useMac ? mac : meterNo;
  return key || String(device.factory_meter_no || '').trim();
}

function crc8(buffer) {
  let crc = 0x00;
  for (let i = 0; i < buffer.length; i++) {
    crc ^= buffer[i];
    for (let b = 0; b < 8; b++) {
      if (crc & 0x80) crc = (crc << 1) ^ 0x07;
      else crc <<= 1;
      crc &= 0xff;
    }
  }
  return crc & 0xff;
}

export function hexToBytes(hexStr) {
  const clean = String(hexStr || '').replace(/\s+/g, '').toLowerCase();
  if (clean.length % 2 !== 0) return null;
  const bytes = [];
  for (let i = 0; i < clean.length; i += 2) {
    const byte = parseInt(clean.substr(i, 2), 16);
    if (Number.isNaN(byte)) return null;
    bytes.push(byte);
  }
  return bytes;
}

export function buildFinalPayload(rawHex, autoCrc = true) {
  const clean = String(rawHex || '').replace(/\s+/g, '').toLowerCase();
  const bytes = hexToBytes(clean);
  if (!bytes) throw new Error('指令格式不合法（需偶数位16进制字符）');
  if (!autoCrc) return clean;
  const crc = crc8(bytes);
  const crcHex = crc.toString(16).padStart(2, '0');
  return clean + crcHex;
}

/** 与 test1：非 BLE 直连时 meterNo 须为 12 位十六进制表号地址 */
export function remotePayloadUseBleDirect(meterKey) {
  const m = String(meterKey || '').trim();
  return !/^[0-9a-fA-F]{12}$/i.test(m);
}

export function buildRemoteReadHex(meterNo, isBleDirect) {
  const m = String(meterNo || '').trim();
  if (!isBleDirect && !/^[0-9a-fA-F]{12}$/.test(m)) {
    throw new Error('表号不合法（非 BLE 直连时需 12 位十六进制地址）');
  }
  return ((isBleDirect ? '' : m) + '51' + '02').toLowerCase();
}

/** 写入剩余电量/水量（绝对值，单位：度或吨） */
export function buildRemoteSetPowerHex(meterNo, power, isBleDirect) {
  const m = String(meterNo || '').trim();
  if (!isBleDirect && !/^[0-9a-fA-F]{12}$/.test(m)) {
    throw new Error('表号不合法（非 BLE 直连时需 12 位十六进制地址）');
  }
  const v = Math.max(0, Number(power) || 0);
  const vInt = Math.round(v * 1000);
  const valueHex = vInt.toString(16).padStart(8, '0');
  return ((isBleDirect ? '' : m) + '45' + '02' + valueHex).toLowerCase();
}

export function getRxAddressCandidates(device) {
  const set = new Set();
  const add = (raw) => {
    let v = String(raw || '').trim().toLowerCase().replace(/:/g, '');
    if (v.length >= 12 && /^[0-9a-f]{12}/.test(v)) set.add(v.slice(0, 12));
  };
  if (!device) return [];
  add(device.device_mac);
  add(device.current_meter_no);
  add(device.factory_meter_no);
  return Array.from(set);
}

/** 8 位 hex 作 32 位有符号大端整数（千分之一度），与小程序 yjMeterEnergyHex8ToSignedMilli 一致 */
function yjMeterEnergyHex8ToSignedMilli(hex8) {
  const h = String(hex8 || '').trim();
  if (h.length !== 8) return 0;
  const n = parseInt(h, 16);
  if (!Number.isFinite(n)) return 0;
  return n | 0;
}

/** 解析 MQTT 下行 51 抄表回调（与 test1 parseRxTopicMessage 一致） */
export function parseMqtt51ReadResponse(rawMsg, addressCandidates) {
  const msg = String(rawMsg || '').trim().toLowerCase().replace(/\s+/g, '');
  // 51 回包最短也能在 34 位内携带 total/remain（带 12 位地址头）；
  // 之前要求 >=60 会把大量有效短报文误判为失败，导致“抄表总超时”。
  if (msg.length < 34) return null;
  const callbackMeterNo = msg.substring(0, 12);
  const candidates = addressCandidates || [];
  if (candidates.length > 0 && !candidates.includes(callbackMeterNo)) return null;
  if (msg.substring(12, 14) !== '51') return null;
  const totalRaw = yjMeterEnergyHex8ToSignedMilli(msg.substring(18, 26));
  const remainRaw = yjMeterEnergyHex8ToSignedMilli(msg.substring(26, 34));
  return { remain: remainRaw / 1000, total: totalRaw / 1000 };
}

/** 与小程序 GET /api/mqtt/message 一致：meter_no、op、sent_at、mapping_fanout_tier（1=首轮 3+2，2=扩展 8+2） */
export function buildMqttMessagePollOpts(publishKey, op, sentAtMs, mappingFanoutTier) {
  const mk = String(publishKey || '').trim().toLowerCase().replace(/:/g, '');
  const opts = { op: op || '' };
  if (/^[0-9a-f]{12}$/.test(mk)) opts.meter_no = mk;
  const n = Number(sentAtMs);
  if (sentAtMs != null && Number.isFinite(n)) opts.sent_at = String(Math.floor(n));
  const tier = Number(mappingFanoutTier) === 2 ? 2 : 1;
  opts.mapping_fanout_tier = tier;
  return opts;
}
